import { useEffect, useState } from "react";
import { showToast } from "../../../../components/CustomToast";
import { OutlinedInput } from "../../../../components/OutlinedInput";
import { FloatingSelect } from "../../../../components/FloatingSelect";
import { i18n } from "../../../../localization/i18n";
import api from "../../../../services/api";
import {
  FormContainer,
  InputLine,
  InputContainer,
} from "../../../Admin/pages/Prices/components/CreatePrice/styles";
import { DetailsContainer, DetailRow, CancelText } from "./styles";
import { IOption, IPlans, Licensee } from "../../dtos";
import * as Yup from "yup";
import { typesErrors } from "../../../../utils/validateFormFields/yupErrors";
import validateFormFields from "../../../../utils/validateFormFields";
import { ISignature } from "../../../../hooks/OnboardContext";
import { FormModal } from "../../../../components/FormModal";

interface SignatureInfoModalImp {
  open: boolean;
  licensee: Licensee;
  licenseeSignature?: ISignature;
  activePlans: IPlans[];
  onRequestClose: () => void;
  handleAssignPlanToPerson: (
    licensee_id: string,
    plan_id?: string | number,
    user_terms_id?: string,
    created_at?: string
  ) => void;
  handleChangeLicenseePlan: (
    licensee_id: string,
    plan_id?: string | number,
    user_terms_id?: string,
    created_at?: string
  ) => void;
  handleDeactivateActiveSignature: () => void;
}

interface IFormData {
  licensee_id?: string;
  plan?: IOption;
  user_terms_id?: string;
  created_at?: string;
}

export function SignatureInfoModal({
  open,
  licensee,
  licenseeSignature,
  activePlans,
  onRequestClose,
  handleAssignPlanToPerson = () => { },
  handleChangeLicenseePlan = () => { },
  handleDeactivateActiveSignature = () => { },
}: SignatureInfoModalImp) {
  const [formData, setFormData] = useState<IFormData>();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({} as any);
  const [planDetails, setPlanDetails] = useState<any>(null);

  useEffect(() => {
    async function fetchPlanDetails() {
      if (formData?.plan?.value) {
        try {
          const response = await api.get(`/plans/${formData.plan.value}`);
          setPlanDetails(response.data);
        } catch (error) {
          showToast({
            type: "error",
            message: "Erro ao buscar detalhes do plano selecionado.",
          });
          setPlanDetails(null);
        }
      } else {
        setPlanDetails(null);
      }
    }
    fetchPlanDetails();
  }, [formData?.plan?.value]);

  useEffect(() => {
    setLoading(false);
    if (licenseeSignature) {
      setFormData({
        plan: {
          optionText: licenseeSignature.plan.plan_name,
          value: licenseeSignature.plan.id,
        },
        user_terms_id: licenseeSignature.user_terms?.id,
        created_at: licenseeSignature.created_at ? new Date(licenseeSignature.created_at).toISOString().slice(0, 10) : "",
      });
    } else {
      setFormData(undefined);
    }
  }, [licensee.signature, licensee.id, licenseeSignature]);

  const formatedPlansToSelect = activePlans.map((plan) => {
    return {
      value: plan.id,
      optionText: plan.plan_name,
    };
  });

  async function AssignOrChangeUserSignature() {
    setLoading(true);
    const body = {
      licensee_id: licensee.id,
      plan_id: formData?.plan?.value,
      user_terms_id: formData?.user_terms_id,
    };

    const bodySchema = Yup.object().shape({
      licensee_id: Yup.string().required(typesErrors.required),
      plan_id: Yup.string().required(typesErrors.required),
    });

    const validation = await validateFormFields(body, bodySchema, {
      licensee_id: "",
      plan_id: "",
    });

    if (validation.status === 400) {
      setErrors(validation.errors);
      showToast({
        type: "error",
        message: i18n.t("form_errors.alert"),
      });
      setLoading(false);
      return;
    }

    setErrors({});

    !licenseeSignature
      ? handleAssignPlanToPerson(
        body.licensee_id,
        body.plan_id,
        body.user_terms_id,
        formData?.created_at
      )
      : handleChangeLicenseePlan(
        licenseeSignature.id,
        body.plan_id,
        body.user_terms_id,
        formData?.created_at
      );
  }

  return (
    <FormModal
      open={open}
      onRequestClose={onRequestClose}
      title={!licenseeSignature ? "Atribuir Assinatura" : "Editar Assinatura"}
      width={500}
      saveText={`${i18n.t("financial.prices.save")}`}
      onSave={AssignOrChangeUserSignature}
      isSaveLoading={loading}
      customLeftFooter={
        licenseeSignature && (
          <CancelText onClick={() => {
            handleDeactivateActiveSignature();
            onRequestClose();
          }}>
            Cancelar assinatura
          </CancelText>
        )
      }
    >
      <FormContainer>
        <InputLine>
          <InputContainer>
            <OutlinedInput
              label="Início da assinatura"
              inputName="created_at"
              type="date"
              value={formData?.created_at || ""}
              handleChange={(val) => {
                setFormData(prev => ({ ...prev, created_at: val }));
              }}
            />
          </InputContainer>
        </InputLine>

        <InputLine>
          <InputContainer>
            <FloatingSelect
              label="Plano"
              value={formData?.plan?.value || ""}
              onChange={(e) => {
                const selectedOption = formatedPlansToSelect.find(p => p.value.toString() === e.target.value);
                setFormData((prevState) => ({ ...prevState, plan: selectedOption }));
              }}
              error={errors.plan_id}
            >
              <option value="" disabled hidden>Selecione um plano</option>
              {formatedPlansToSelect.map(plan => (
                <option key={plan.value} value={plan.value}>{plan.optionText}</option>
              ))}
            </FloatingSelect>
          </InputContainer>
        </InputLine>
        
        {planDetails && (
          <DetailsContainer>
            <DetailRow>
              <strong>Valor do Plano: </strong>
              {planDetails?.price !== undefined && planDetails?.price !== null
                ? `${planDetails.currency === 'USD' ? '$' : 'R$'} ${planDetails.price}` 
                : "Não definido"}
            </DetailRow>
            <DetailRow>
              <strong>Tabela de Preços: </strong>
              {planDetails?.price_table?.name || "Não definida"}
            </DetailRow>
            <DetailRow>
              <strong>Moeda: </strong>
              {planDetails?.currency || "Não definida"}
            </DetailRow>
          </DetailsContainer>
        )}
      </FormContainer>
    </FormModal>
  );
}